import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input, Button, Divider } from 'antd';
import { SearchOutlined, PlusOutlined, LeftOutlined, RightOutlined, EllipsisOutlined } from '@ant-design/icons';
import PSAIModal from '@PSAIComponents/PSAIModal';
import ProjectContextMenu from './components/ProjectContextMenu';
import styles from './index.module.scss';

const DRAG_SCROLL_EDGE_THRESHOLD = 36;
const DRAG_SCROLL_STEP = 14;
const PIN_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/pin.svg';
const CONTEXT_MENU_VIEWPORT_MARGIN = 8;
const CONTEXT_MENU_ITEM_HEIGHT = 32;
const CONTEXT_MENU_GAP = 2;
const CONTEXT_MENU_VERTICAL_PADDING = 16;

const parseProjectTime = (value = '') => {
    const matched = String(value).match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/,
    );
    if (!matched) return null;

    const [, year, month, day, hour, minute, second] = matched;
    return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
    );
};

const formatProjectUpdatedText = (value = '') => {
    const updatedAt = parseProjectTime(value);
    if (!updatedAt || Number.isNaN(updatedAt.getTime())) return '';

    const diffMs = Math.max(0, Date.now() - updatedAt.getTime());
    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;
    const monthMs = 30 * dayMs;

    if (diffMs < hourMs) {
        return `${Math.max(1, Math.floor(diffMs / minuteMs))}分钟前 修改`;
    }

    if (diffMs < dayMs) {
        return `${Math.floor(diffMs / hourMs)}小时前 修改`;
    }

    if (diffMs < monthMs) {
        return `${Math.floor(diffMs / dayMs)}天前 修改`;
    }

    return '1个月前 修改';
};

const Sidebar = ({
    projects = [],
    activeId,
    keyword: keywordProp = '',
    onSelect,
    onCreate,
    collapsed,
    onCollapsedChange,
    onKeywordChange,
    onLoadMore,
    onPinProject,
    onDuplicateProject,
    onSortProject,
    onRenameProject,
    onDeleteProject,
    hasMore = false,
    loading = false,
    readOnlyView = false,
}) => {
    const [keyword, setKeyword] = useState('');
    const [editingId, setEditingId] = useState('');
    const [editingValue, setEditingValue] = useState('');
    const [draggingProjectId, setDraggingProjectId] = useState('');
    const [dropTarget, setDropTarget] = useState({ projectId: '', position: '' });
    const [deleteModalProjectId, setDeleteModalProjectId] = useState('');
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        projectId: '',
        pinned: false,
    });
    const editingInputRef = useRef(null);
    const listRef = useRef(null);
    const dragScrollFrameRef = useRef(null);
    const dragScrollDirectionRef = useRef(0);
    const pendingActiveScrollRef = useRef(false);

    const handleKeywordChange = (event) => {
        const nextKeyword = event.target.value;
        setKeyword(nextKeyword);
        onKeywordChange?.(nextKeyword);
    };

    useEffect(() => {
        setKeyword((prev) => (prev === keywordProp ? prev : keywordProp));
    }, [keywordProp]);

    const handleListScroll = (event) => {
        if (draggingProjectId) return;
        if (!hasMore || loading) return;

        const target = event.currentTarget;
        const remain = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (remain <= 40) {
            onLoadMore?.();
        }
    };

    useEffect(() => {
        if (!editingId || !editingInputRef.current) return;
        editingInputRef.current.focus();
        editingInputRef.current.select();
    }, [editingId]);

    useEffect(() => {
        pendingActiveScrollRef.current = !!activeId;
    }, [activeId]);

    useEffect(() => {
        if (!pendingActiveScrollRef.current || !activeId || !listRef.current) return;

        const activeItemElement = listRef.current.querySelector(`[data-project-id="${activeId}"]`);
        if (!activeItemElement) return;

        activeItemElement.scrollIntoView({
            block: 'nearest',
        });
        pendingActiveScrollRef.current = false;
    }, [activeId, projects]);

    useEffect(() => {
        if (!contextMenu.visible) return undefined;

        const handlePointerDown = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (target?.closest('[data-free-canvas-sidebar-menu="true"]')) return;
            setContextMenu((prev) => ({ ...prev, visible: false }));
        };

        document.addEventListener('mousedown', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [contextMenu.visible]);

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const resetDragState = () => {
        setDraggingProjectId('');
        setDropTarget({ projectId: '', position: '' });
        dragScrollDirectionRef.current = 0;
        if (dragScrollFrameRef.current) {
            cancelAnimationFrame(dragScrollFrameRef.current);
            dragScrollFrameRef.current = null;
        }
    };

    const runDragAutoScroll = () => {
        const listElement = listRef.current;
        const direction = dragScrollDirectionRef.current;

        if (!listElement || direction === 0) {
            dragScrollFrameRef.current = null;
            return;
        }

        listElement.scrollTop += DRAG_SCROLL_STEP * direction;
        dragScrollFrameRef.current = requestAnimationFrame(runDragAutoScroll);
    };

    const updateDragAutoScroll = (clientY) => {
        const listElement = listRef.current;
        if (!listElement) return;

        const rect = listElement.getBoundingClientRect();
        let nextDirection = 0;

        if (clientY <= rect.top + DRAG_SCROLL_EDGE_THRESHOLD) {
            nextDirection = -1;
        } else if (clientY >= rect.bottom - DRAG_SCROLL_EDGE_THRESHOLD) {
            nextDirection = 1;
        }

        if (nextDirection === dragScrollDirectionRef.current) return;

        dragScrollDirectionRef.current = nextDirection;

        if (dragScrollFrameRef.current) {
            cancelAnimationFrame(dragScrollFrameRef.current);
            dragScrollFrameRef.current = null;
        }

        if (nextDirection !== 0) {
            dragScrollFrameRef.current = requestAnimationFrame(runDragAutoScroll);
        }
    };

    const startRename = (projectId) => {
        const targetProject = projects.find((item) => item.id === projectId);
        if (!targetProject) return;
        setEditingId(projectId);
        setEditingValue(targetProject.name);
    };

    const handleRenameSubmit = async () => {
        if (!editingId) return;

        const targetProject = projects.find((item) => item.id === editingId);
        const trimmedValue = editingValue.trim();

        if (!targetProject) {
            setEditingId('');
            setEditingValue('');
            return;
        }

        if (!trimmedValue || trimmedValue === targetProject.name) {
            setEditingId('');
            setEditingValue('');
            return;
        }

        const saved = await onRenameProject?.(editingId, trimmedValue);
        if (saved === false) return;

        setEditingId('');
        setEditingValue('');
    };

    const handleRenameCancel = () => {
        setEditingId('');
        setEditingValue('');
    };

    const handleMenuTriggerClick = (event, projectId) => {
        event.stopPropagation();
        onSelect?.(projectId);
        const triggerRect = event.currentTarget.getBoundingClientRect();
        const targetProject = projects.find((item) => item.id === projectId);
        const menuItemCount = [
            true,
            true,
            true,
            false,
            true,
        ].filter(Boolean).length;
        const menuHeight = CONTEXT_MENU_VERTICAL_PADDING
            + (menuItemCount * CONTEXT_MENU_ITEM_HEIGHT)
            + (Math.max(0, menuItemCount - 1) * CONTEXT_MENU_GAP);
        const preferredTop = triggerRect.bottom + 8;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const nextTop = preferredTop + menuHeight + CONTEXT_MENU_VIEWPORT_MARGIN > viewportHeight
            ? Math.max(CONTEXT_MENU_VIEWPORT_MARGIN, triggerRect.top - 8 - menuHeight)
            : preferredTop;
        setContextMenu({
            visible: true,
            x: triggerRect.right - 80,
            y: nextTop,
            projectId,
            pinned: !!targetProject?.pinned,
        });
    };

    const handleMenuRename = () => {
        const { projectId } = contextMenu;
        closeContextMenu();
        startRename(projectId);
    };

    const handleMenuDuplicate = async () => {
        const { projectId } = contextMenu;
        closeContextMenu();
        await onDuplicateProject?.(projectId);
    };

    const handleMenuPin = async () => {
        const { projectId } = contextMenu;
        closeContextMenu();
        await onPinProject?.(projectId);
    };

    const handleMenuDelete = () => {
        const { projectId } = contextMenu;
        closeContextMenu();
        setDeleteModalProjectId(projectId);
    };

    const handleDeleteModalClose = () => {
        if (deleteSubmitting) return;
        setDeleteModalProjectId('');
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModalProjectId || deleteSubmitting) return;

        setDeleteSubmitting(true);
        try {
            const deleted = await onDeleteProject?.(deleteModalProjectId);
            if (deleted === false) return;
            setDeleteModalProjectId('');
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const pinnedProjectCount = useMemo(
        () => projects.filter((item) => !!item.pinned).length,
        [projects],
    );
    const firstUnpinnedProject = useMemo(
        () => projects.find((item) => !item.pinned) || null,
        [projects],
    );

    const getDropPosition = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        return event.clientY <= rect.top + rect.height / 2 ? 'before' : 'after';
    };

    const buildReorderedIndex = useMemo(() => {
        const projectIndexMap = new Map(projects.map((item, index) => [item.id, index]));
        return (projectId, targetProjectId, position) => {
            const oldIdx = projectIndexMap.get(projectId);
            const targetIdx = projectIndexMap.get(targetProjectId);

            if (
                oldIdx === undefined
                || targetIdx === undefined
                || !position
                || projectId === targetProjectId
            ) {
                return { oldIdx: -1, newIdx: -1 };
            }

            const newIdx = position === 'before'
                ? (oldIdx < targetIdx ? targetIdx - 1 : targetIdx)
                : (oldIdx < targetIdx ? targetIdx : targetIdx + 1);

            return { oldIdx, newIdx };
        };
    }, [projects]);

    const canDropProjectAt = (projectId, targetProjectId, position) => {
        const draggedProject = projects.find((item) => item.id === projectId);
        if (!draggedProject || draggedProject.pinned) return false;

        const targetProject = projects.find((item) => item.id === targetProjectId);
        if (!targetProject) return false;

        if (targetProject.pinned) {
            const targetIndex = projects.findIndex((item) => item.id === targetProjectId);
            return position === 'after' && targetIndex === pinnedProjectCount - 1;
        }

        const { oldIdx, newIdx } = buildReorderedIndex(projectId, targetProjectId, position);
        if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return false;

        return newIdx >= pinnedProjectCount;
    };

    const handleDragStart = (event, projectId) => {
        const draggedProject = projects.find((item) => item.id === projectId);
        if (draggedProject?.pinned) {
            event.preventDefault();
            return;
        }

        setDraggingProjectId(projectId);
        closeContextMenu();
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', projectId);
    };

    const handleDragOverItem = (event, projectId) => {
        if (!draggingProjectId || draggingProjectId === projectId) return;
        const position = getDropPosition(event);
        if (firstUnpinnedProject?.id === projectId && position === 'before') {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            updateDragAutoScroll(event.clientY);
            setDropTarget({
                projectId,
                position,
            });
            return;
        }

        const canDrop = canDropProjectAt(draggingProjectId, projectId, position);
        if (!canDrop) {
            setDropTarget((prev) => (
                prev.projectId || prev.position
                    ? { projectId: '', position: '' }
                    : prev
            ));
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        updateDragAutoScroll(event.clientY);
        setDropTarget({
            projectId,
            position,
        });
    };

    const handleDropItem = async (event, targetProjectId) => {
        event.preventDefault();

        const draggedProjectId = event.dataTransfer.getData('text/plain') || draggingProjectId;
        const position = getDropPosition(event);
        if (!canDropProjectAt(draggedProjectId, targetProjectId, position)) {
            resetDragState();
            return;
        }

        resetDragState();

        await onSortProject?.({
            id: draggedProjectId,
            targetProjectId,
            position,
        });
    };

    const handlePinnedBoundaryDragOver = (event) => {
        if (!draggingProjectId || !firstUnpinnedProject) return;
        if (!canDropProjectAt(draggingProjectId, firstUnpinnedProject.id, 'before')) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        updateDragAutoScroll(event.clientY);
        setDropTarget({
            projectId: firstUnpinnedProject.id,
            position: 'before',
        });
    };

    const handlePinnedBoundaryDrop = async (event) => {
        if (!firstUnpinnedProject) {
            resetDragState();
            return;
        }

        event.preventDefault();
        const draggedProjectId = event.dataTransfer.getData('text/plain') || draggingProjectId;
        if (!canDropProjectAt(draggedProjectId, firstUnpinnedProject.id, 'before')) {
            resetDragState();
            return;
        }

        resetDragState();

        await onSortProject?.({
            id: draggedProjectId,
            targetProjectId: firstUnpinnedProject.id,
            position: 'before',
        });
    };

    const handleDragEnd = () => {
        resetDragState();
    };

    return (
        <div className={`${styles.wrapper} ${collapsed ? styles.collapsed : ''}`}>
            <div className={styles.panel}>
                <div className={styles.header}>{readOnlyView ? '画布列表' : '我的画布'}</div>
                <Input
                    className={styles.search}
                    placeholder="搜索画布..."
                    prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                    allowClear
                    value={keyword}
                    onChange={handleKeywordChange}
                />
                <Divider style={{ margin: '0' }} />
                <div
                    ref={listRef}
                    className={styles.list}
                    onScroll={handleListScroll}
                    onDragOver={(event) => {
                        if (readOnlyView) return;
                        if (!draggingProjectId) return;
                        const draggedProject = projects.find((item) => item.id === draggingProjectId);
                        if (draggedProject?.pinned) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        updateDragAutoScroll(event.clientY);
                    }}
                >
                    {projects.map((item) => (
                        <React.Fragment key={item.id}>
                            {firstUnpinnedProject?.id === item.id && pinnedProjectCount > 0 ? (
                                <div
                                    className={`${styles.pinnedBoundary} ${dropTarget.projectId === item.id && dropTarget.position === 'before' ? styles.pinnedBoundaryActive : ''}`}
                                    onDragOver={handlePinnedBoundaryDragOver}
                                    onDrop={handlePinnedBoundaryDrop}
                                />
                            ) : null}
                        <div
                            data-project-id={item.id}
                            className={`${styles.item} ${activeId === item.id ? styles.active : ''} ${draggingProjectId === item.id ? styles.dragging : ''} ${dropTarget.projectId === item.id && dropTarget.position === 'before' ? styles.dropBefore : ''} ${dropTarget.projectId === item.id && dropTarget.position === 'after' ? styles.dropAfter : ''}`}
                            onClick={() => onSelect?.(item.id)}
                            draggable={!readOnlyView && editingId !== item.id && !item.pinned}
                            onDragStart={(event) => handleDragStart(event, item.id)}
                            onDragOver={(event) => handleDragOverItem(event, item.id)}
                            onDrop={(event) => handleDropItem(event, item.id)}
                            onDragEnd={handleDragEnd}
                        >
                            <div className={styles.itemMain}>
                                {editingId === item.id ? (
                                    <input
                                        ref={editingInputRef}
                                        className={styles.renameInput}
                                        value={editingValue}
                                        maxLength={20}
                                        onChange={(event) => setEditingValue(event.target.value)}
                                        onClick={(event) => event.stopPropagation()}
                                        onBlur={handleRenameSubmit}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                handleRenameSubmit();
                                            } else if (event.key === 'Escape') {
                                                handleRenameCancel();
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className={styles.itemText}>
                                        <span className={styles.itemName}>
                                            {item.pinned ? (
                                                <img
                                                    src={PIN_ICON}
                                                    alt=""
                                                    width={16}
                                                    height={16}
                                                    className={styles.pinIcon}
                                                    draggable={false}
                                                />
                                            ) : null}
                                            <span className={styles.itemNameText} title={item.name || ''}>{item.name}</span>
                                        </span>
                                        <span className={styles.itemId}>{formatProjectUpdatedText(item.updatedTime)}</span>
                                    </div>
                                )}
                            </div>
                            {!readOnlyView && editingId !== item.id ? (
                                <button
                                    type="button"
                                    className={styles.menuTrigger}
                                    onClick={(event) => handleMenuTriggerClick(event, item.id)}
                                    aria-label={`更多操作-${item.name}`}
                                >
                                    <EllipsisOutlined style={{ fontSize: 16 }} />
                                </button>
                            ) : null}
                        </div>
                        </React.Fragment>
                    ))}
                    {loading ? <div className={styles.status}>加载中...</div> : null}
                    {!loading && projects.length === 0 ? <div className={styles.status}>暂无项目</div> : null}
                </div>
                <div className={styles.footer}>
                    <Button
                        style={{ height: 36 }}
                        type="primary"
                        icon={<PlusOutlined />}
                        block
                        onClick={readOnlyView ? undefined : onCreate}
                        disabled={readOnlyView}
                    >
                        新建项目
                    </Button>
                </div>
            </div>
            <div className={styles.toggle} onClick={() => onCollapsedChange?.(!collapsed)}>
                {collapsed ? <RightOutlined style={{ fontSize: 12 }} /> : <LeftOutlined style={{ fontSize: 12 }} />}
            </div>
            <ProjectContextMenu
                visible={!readOnlyView && contextMenu.visible}
                x={contextMenu.x}
                y={contextMenu.y}
                pinned={contextMenu.pinned}
                onPin={handleMenuPin}
                onDuplicate={handleMenuDuplicate}
                onRename={handleMenuRename}
                onDelete={handleMenuDelete}
            />
            <PSAIModal
                title="删除内容"
                open={!readOnlyView && !!deleteModalProjectId}
                maxWidth={460}
                maskClosable={false}
                closable={!deleteSubmitting}
                footer={(
                    <div className={styles.deleteModalFooter}>
                        <Button
                            className={styles.deleteModalButton}
                            onClick={handleDeleteModalClose}
                            disabled={deleteSubmitting}
                        >
                            取消
                        </Button>
                        <Button
                            className={styles.deleteModalButton}
                            type="primary"
                            loading={deleteSubmitting}
                            onClick={handleDeleteConfirm}
                        >
                            删除
                        </Button>
                    </div>
                )}
                onCancel={handleDeleteModalClose}
            >
                <div className={styles.deleteModalContent}>
                    <div className={styles.deleteModalTitle}>确定要删除此画布项目吗?</div>
                    <div className={styles.deleteModalWarning}>此操作不可复原，请确认是否删除</div>
                </div>
            </PSAIModal>
        </div>
    );
};

export default Sidebar;
